import React, { useEffect, useState, useRef } from 'react';
import api from '../services/api';
import ProfileForm from '../components/ProfileForm';
import DocumentUpload from '../components/DocumentUpload';
import DocumentList from '../components/DocumentList';
import { FaEye, FaDownload, FaEdit, FaTrash, FaPlus } from 'react-icons/fa';

// ensure apiUrl is defined (adjust REACT_APP_API_URL in .env if used)
const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const UserDashboard = ({ viewUserId = null }) => {
  const [profile, setProfile] = useState(null);
  const [consultants, setConsultants] = useState([]); // all consultants (used to map id -> consultant)
  const [assignedConsultant, setAssignedConsultant] = useState(null); // resolved consultant for current user
  const [profilePic, setProfilePic] = useState(null);
  const [picPreview, setPicPreview] = useState('');
  const [documents, setDocuments] = useState([]);
  const [groupedDocs, setGroupedDocs] = useState({});
  const [loading, setLoading] = useState(true);
  const [permanentDocs, setPermanentDocs] = useState([]);
  const [periodicalDocs, setPeriodicalDocs] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editDoc, setEditDoc] = useState(null);
  const [editForm, setEditForm] = useState({ docType: '', docYear: '', remarks: '' });
  const modalFileRef = useRef(null);

  const [showUploadFor, setShowUploadFor] = useState(null);
  const openUpload = (section) => setShowUploadFor(section);
  const closeUpload = () => setShowUploadFor(null);

  // missing states/helpers
  const fileInputRef = useRef(null);
  const [showProfileForm, setShowProfileForm] = useState(false);
  const isBlank = (v) => v === null || v === undefined || (typeof v === 'string' && v.trim() === '');

  // new states for asset/liability details
  const [showAssetsOpen, setShowAssetsOpen] = useState(false);
  const [showLiabsOpen, setShowLiabsOpen] = useState(false);
  const [subscription, setSubscription] = useState(null);
  // helper to safely parse list fields
  const parseList = (val) => {
    if (!val) return [];
    try {
      return typeof val === 'string' ? JSON.parse(val) : Array.isArray(val) ? val : [];
    } catch {
      return [];
    }
  };
  
  // parse and render other_info (returns null when no meaningful entries)
  const parseOtherInfo = (other) => {
    if (!other) return [];
    try {
      const parsed = typeof other === 'string' ? JSON.parse(other) : other;
      if (Array.isArray(parsed)) {
        return parsed.map(o => ({ key: o.key ?? o.name ?? '', value: o.value ?? o.info ?? '' }));
      }
      if (parsed && typeof parsed === 'object') {
        return Object.entries(parsed).map(([k, v]) => ({ key: k, value: v ?? '' }));
      }
      return [{ key: '', value: String(parsed) }];
    } catch {
      return [{ key: '', value: String(other) }];
    }
  };

  const renderOtherInfo = (other) => {
    const list = parseOtherInfo(other).filter(it => (it.key && String(it.key).trim()) || (it.value && String(it.value).trim()));
    if (!list || list.length === 0) return null;
    return (
      <div style={{ marginTop: 8 }}>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>Other</div>
        <div style={{ color: '#333' }}>
          {list.map((it, i) => (
            <div key={i}>{it.key ? `${it.key}: ${it.value}` : it.value}</div>
          ))}
        </div>
      </div>
    );
  };

  // compute assets / liabilities derived values from profile
  const assetsList = parseList(profile?.asset_details);
  const liabilitiesList = parseList(profile?.liability_details);
  const assetsTotal = assetsList.reduce((s, a) => s + (Number(a?.amount) || 0), 0);
  const liabilitiesTotal = liabilitiesList.reduce((s, l) => s + (Number(l?.amount) || 0), 0);

  // helper to refresh documents (prefer explicit user id; fallback to viewUserId or profile.id)
  const refreshDocs = async (targetUserId = null) => {
    try {
      const uid = targetUserId ?? viewUserId ?? profile?.id ?? null;
      const docsEndpoint = uid ? `/documents/user/${uid}` : '/documents/user/me';
      const res = await api.get(docsEndpoint);
      let docs = res.data || [];

      // Ensure latest uploaded documents appear first.
      // Prefer created_at (ISO/DB timestamp) descending; fallback to doc_year (e.g. "2023-24") descending.
      const parseTime = (v) => {
        if (!v) return 0;
        const t = Date.parse(String(v));
        return Number.isNaN(t) ? 0 : t;
      };
      docs.sort((a, b) => {
        const ta = parseTime(a.created_at);
        const tb = parseTime(b.created_at);
        if (ta !== tb) return tb - ta;
        const ya = parseInt(String(a.doc_year || '').slice(0, 4)) || 0;
        const yb = parseInt(String(b.doc_year || '').slice(0, 4)) || 0;
        return yb - ya;
      });

      const perm = [];
      const peri = [];

      docs.forEach((doc) => {
        const t = (doc.doc_type || '').trim();
        const cat = (doc.category || '').toLowerCase();

        if (cat === 'permanent') { perm.push(doc); return; }
        if (cat === 'periodical') { peri.push(doc); return; }

        const tLower = t.toLowerCase();
        if (PERMANENT_DOCS.map(s => s.toLowerCase()).includes(tLower)) perm.push(doc);
        else if (PERIODICAL_DOCS.map(s => s.toLowerCase()).includes(tLower)) peri.push(doc);
        else peri.push(doc);
      });

      // helper: extract first 4-digit year from doc_year string
      const yearFromDoc = (d) => {
        if (!d || !d.doc_year) return 0;
        const m = String(d.doc_year).match(/(\d{4})/);
        return m ? parseInt(m[1], 10) : 0;
      };
 
      // sort permanent docs by created_at (newest first)
      perm.sort((a, b) => parseTime(b.created_at) - parseTime(a.created_at));
 
      // sort periodical docs by extracted year (first 4 digits) desc, then created_at desc
      peri.sort((a, b) => {
        const ya = yearFromDoc(a);
        const yb = yearFromDoc(b);
        if (ya !== yb) return yb - ya;
        return parseTime(b.created_at) - parseTime(a.created_at);
      });
 
      setDocuments(docs);
      setPermanentDocs(perm);
      setPeriodicalDocs(peri);
    } catch (err) {
      console.error('Failed to refresh documents', err);
    }
  };

  // upload helper (used when avatar icon selects a file)
  const uploadProfilePic = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append('file', file);
      // if viewing another user, update their profile endpoint; otherwise use profile-picture endpoint
      if (viewUserId) {
        // reuse update profile endpoint to accept file (backend routes support PUT /users/:id/profile)
        await api.put(`/users/${viewUserId}/profile`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        const profileRes = await api.get(`/users/${viewUserId}/profile`);
        setProfile(profileRes.data);
      } else {
        await api.post('/users/profile-picture', fd);
        const profileRes = await api.get('/users/profile');
        setProfile(profileRes.data);
      }
      setPicPreview('');
      setProfilePic(null);
    } catch (err) {
      console.error('Profile picture upload failed', err);
      alert(err?.response?.data?.message || 'Upload failed');
    }
  };

  // Format a YYYY-MM-DD or ISO string as dd-mm-yyyy (avoid TZ shift)
  const formatDateOnly = (s) => {
    if (!s) return '-';
    // prefer YYYY-MM-DD input and reformat directly to avoid timezone shifts
    const ymd = String(s).split('T')[0];
    const parts = ymd.split('-');
    if (parts.length < 3) return String(s);
    const [y, m, d] = parts;
    const dd = String(d).padStart(2, '0');
    const mm = String(m).padStart(2, '0');
    const yyyy = String(y);
    return `${dd}-${mm}-${yyyy}`;
  };

  // Fetch profile and documents — if viewUserId provided, fetch that user's data, otherwise fetch current user
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const profileEndpoint = viewUserId ? `/users/${viewUserId}/profile` : '/users/profile';
        const res = await api.get(profileEndpoint);
        setProfile(res.data);
 
        // load consultants list (optional)
        // try to load consultants list (may be forbidden for regular users)
        let found = null;
        try {
          const listRes = await api.get('/users/list');
          setConsultants(listRes.data || []);
          // resolve assigned consultant id from profile (several possible keys)
          const cid = res.data?.consultant_id ?? res.data?.assigned_consultant_id ?? res.data?.assigned_consultant ?? res.data?.consultant?.id ?? res.data?.consultant_id;
          found = (listRes.data || []).find(c =>
            String(c.id) === String(cid) ||
            String(c.user_id) === String(cid) ||
            (c.email && c.email === cid)
          ) || null;
        } catch (e) {
          // users/list may be forbidden for plain users — we'll fallback to consultant info included in profile
          console.debug('Could not fetch users/list (may be forbidden):', e?.response?.status || e?.message);
        }

        // if we found consultant via list use it; otherwise fallback to profile.consultant or minimal object from consultant_id
        if (found) {
          setAssignedConsultant(found);
        } else {
          const profCons = res.data?.consultant ?? null;
          if (profCons) {
            setAssignedConsultant(profCons);
          } else if (res.data?.consultant_id) {
            setAssignedConsultant({ id: res.data.consultant_id, name: null, email: null, mobile: null, profile_picture: null });
          } else {
            setAssignedConsultant(null);
          }
        }

        // load documents for current/viewed user — pass explicit id so backend route /documents/user/:id is used
        await refreshDocs(res.data?.id ?? null);
        // optional: load subscription for this user only when enabled via env var
        // set REACT_APP_ENABLE_SUBSCRIPTIONS=true to enable this request
        if (process.env.REACT_APP_ENABLE_SUBSCRIPTIONS === 'true') {
          try {
            const sid = res.data?.id;
            if (sid) {
              const sres = await api.get(`/subscriptions/user/${sid}`);
              const sdata = Array.isArray(sres.data) ? (sres.data[0] || null) : sres.data;
              if (sdata) setSubscription(sdata);
            }
          } catch (e) {
            // keep quiet (endpoint optional) but debug info available in console when needed
            console.debug('Subscription fetch skipped/failed:', e?.response?.status || e?.message);
          }
        }
      } catch (err) {
        console.error('Failed to fetch profile', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [viewUserId]);

  // add missing handler to preview profile pic selection
  const handlePicChange = async (e) => {
    const f = e?.target?.files?.[0] ?? null;
    setProfilePic(f);
    if (f) {
      const reader = new FileReader();
      reader.onload = () => setPicPreview(reader.result);
      reader.readAsDataURL(f);
      try {
        // immediately upload the selected picture (handles consultant uploading client when viewUserId is set)
        await uploadProfilePic(f);
      } catch (err) {
        console.error('Auto upload profile picture failed', err);
        alert(err?.response?.data?.message || 'Profile picture upload failed');
      }
    } else {
      setPicPreview('');
    }
  };

  // show update modal for a document (initialize controlled form)
  const handleUpdate = (doc) => {
    setEditDoc(doc);
    setEditForm({
      docType: doc.doc_type || '',
      docYear: doc.doc_year || '',
      remarks: doc.remarks || ''
    });
    // reset modal file input if open earlier
    if (modalFileRef.current) modalFileRef.current.value = '';
    setShowModal(true);
  };

  // delete document and refresh
  const handleDelete = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    try {
      await api.delete(`/documents/${docId}`);
      await refreshDocs();
    } catch (err) {
      console.error('Delete failed', err);
      alert(err?.response?.data?.message || 'Delete failed');
    }
  };

  if (loading) return <div>Loading user dashboard...</div>;
  if (!profile) return <div>No profile found.</div>;

  return (
    <div style={{ maxWidth: 900, margin: '2rem auto', background: '#fff', borderRadius: 12, boxShadow: '0 4px 24px #0002', padding: '2rem' }}>
      <h2 style={{ color: '#1976d2', textAlign: 'center', marginBottom: '2rem' }}>{profile?.name || 'User Dashboard'}</h2>

      {/* Document update modal */}
      {showModal && editDoc && (
        <div style={{ position: 'fixed', inset: 0, background: '#0008', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              try {
                const fd = new FormData();
                fd.append('docType', editForm.docType);
                fd.append('docYear', editForm.docYear || '');
                fd.append('remarks', editForm.remarks || '');
                const file = modalFileRef.current?.files?.[0];
                if (file) fd.append('file', file);
                await api.put(`/documents/${editDoc.id}`, fd);
                setShowModal(false);
                setEditDoc(null);
                setEditForm({ docType: '', docYear: '', remarks: '' });
                if (modalFileRef.current) modalFileRef.current.value = '';
                await refreshDocs();
              } catch (err) {
                console.error('Update failed', err);
                alert(err?.response?.data?.message || 'Update failed');
              }
            }}
            style={{ background: '#fff', padding: 20, borderRadius: 8, width: 520, maxWidth: '95%' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>Update Document</h3>
              <button type="button" onClick={() => { setShowModal(false); setEditDoc(null); }} style={{ background: 'none', border: 'none', fontSize: 20 }}>✕</button>
            </div>
            <div style={{ display: 'grid', gap: 8 }}>
              <label htmlFor="edit-docType">File Type</label>
              <input id="edit-docType" name="docType" value={editForm.docType} onChange={e => setEditForm({ ...editForm, docType: e.target.value })} required />

              <label htmlFor="edit-docYear">Year (optional)</label>
              <input id="edit-docYear" name="docYear" value={editForm.docYear} onChange={e => setEditForm({ ...editForm, docYear: e.target.value })} />

              <label htmlFor="edit-remarks">Remarks</label>
              <input id="edit-remarks" name="remarks" value={editForm.remarks} onChange={e => setEditForm({ ...editForm, remarks: e.target.value })} />

              <div>
                <label>Existing file: </label>
                <a href={apiUrl + editDoc.file_url} target="_blank" rel="noreferrer" style={{ color: '#1976d2' }}>{editDoc.file_name || 'View'}</a>
              </div>

              <div>
                <label htmlFor="edit-file">Replace file (optional)</label>
                <input id="edit-file" ref={modalFileRef} name="file" type="file" />
              </div>

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
                <button type="submit" style={uploadBtnStyle}>Save</button>
                <button type="button" onClick={() => { setShowModal(false); setEditDoc(null); setEditForm({ docType: '', docYear: '', remarks: '' }); }} style={{ ...uploadBtnStyle, background: '#888' }}>Cancel</button>
              </div>
            </div>
          </form>
        </div>
      )}

      {showProfileForm ? (
        /* FULL-PAGE EDIT MODE - show profile picture at top and form below (full page) */
        <div style={{ display: 'grid', gap: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <div style={{ position: 'relative', textAlign: 'center' }}>
              <img
                src={picPreview || (profile?.profile_picture ? apiUrl + profile.profile_picture : `${process.env.PUBLIC_URL || ''}/image/profile-icon.jpg`)}
                alt="Profile"
                style={{ width: 200, height: 200, borderRadius: '50%', objectFit: 'cover', border: '3px solid #1976d2' }}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                title="Change profile picture"
                style={{
                  position: 'absolute', right: 8, bottom: 8, background: '#fff', borderRadius: '50%',
                  border: '1px solid #e2e8f0', padding: 10, cursor: 'pointer'
                }}
              >
                <FaEdit style={{ color: '#1976d2' }} />
              </button>
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handlePicChange} style={{ display: 'none' }} />
              {/* Name intentionally hidden in edit mode per request */}
            </div>
          </div>

          <div style={{ maxWidth: 900, margin: '0 auto', width: '100%', background: '#f9fbff', padding: 20, borderRadius: 8 }}>
            {/* Page-width ProfileForm - keep file input off (we handle avatar above) */}
            <ProfileForm
              profile={profile}
              targetUserId={viewUserId || profile?.id}
              onSaved={(updated) => { setProfile(updated); setShowProfileForm(false); }}
              showFileInput={false}
            />
          </div>
        </div>
      ) : (
        /* Normal dashboard (profile summary + documents) */
        <div style={{ display: 'flex', gap: '2rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {/* Profile Card */}
          <div style={{ position: 'relative', flex: '1 1 250px', background: '#f5f8fa', borderRadius: 8, padding: '1.5rem', boxShadow: '0 2px 8px #0001', minWidth: 250, textAlign: 'center' }}>
            {/* top-right edit icon */}
            <button
              onClick={() => setShowProfileForm(s => !s)}
              title="Edit profile"
              style={{
                position: 'absolute', top: 10, right: 10,
                background: 'none', border: 'none', padding: 6, cursor: 'pointer'
              }}
            >
              <FaEdit style={{ color: '#1976d2', fontSize: 16 }} />
            </button>

            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8 }}>
              <div style={{ position: 'relative' }}>
                <img
                  src={picPreview || (profile?.profile_picture ? apiUrl + profile.profile_picture : `${process.env.PUBLIC_URL || ''}/image/profile-icon.jpg`)}
                  alt="Profile"
                  style={{ width: 100, height: 100, borderRadius: '50%', objectFit: 'cover', marginBottom: 12, border: '2px solid #1976d2' }}
                />
                {/* avatar edit overlay only visible in edit mode */}
                {showProfileForm && (
                  <>
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      title="Change profile picture"
                      style={{
                        position: 'absolute', right: -6, bottom: -6, background: '#fff', borderRadius: '50%',
                        border: '1px solid #e2e8f0', padding: 8, cursor: 'pointer'
                      }}
                    >
                      <FaEdit style={{ color: '#1976d2' }} />
                    </button>
                    <input ref={fileInputRef} type="file" accept="image/*" onChange={handlePicChange} style={{ display: 'none' }} />
                  </>
                )}
              </div>
            </div>

            {/* PROFILE FIELDS (exclude name) - show only non-empty values */}
            {!showProfileForm && (
              <div style={{ textAlign: 'left', marginTop: 12 }}>

                {!isBlank(profile?.profession) && <div style={{ marginBottom: 6 }}>Profession: <b>{profile.profession}</b></div>}
                {!isBlank(profile?.date_of_birth) && <div style={{ marginBottom: 6 }}>Date of birth: <b>{formatDateOnly(profile.date_of_birth)}</b></div>}
                {!isBlank(profile?.registered_mobile) && <div style={{ marginBottom: 6 }}>Registered Mobile: <b>{profile.registered_mobile}</b></div>}
                {!isBlank(profile?.mobile) && <div style={{ marginBottom: 6 }}>Mobile: <b>{profile.mobile}</b></div>}
                {!isBlank(profile?.tin) && <div style={{ marginBottom: 6 }}>TIN: <b>{profile.tin}</b></div>}
                {!isBlank(profile?.nid_no) && <div style={{ marginBottom: 6 }}>NID No: <b>{profile.nid_no}</b></div>}
                {!isBlank(profile?.blood_group) && <div style={{ marginBottom: 6 }}>Blood Group: <b>{profile.blood_group}</b></div>}
                {!isBlank(profile?.father_name) && <div style={{ marginBottom: 6 }}>Father: <b>{profile.father_name}</b></div>}
                {!isBlank(profile?.mother_name) && <div style={{ marginBottom: 6 }}>Mother: <b>{profile.mother_name}</b></div>}
                {!isBlank(profile?.spouse_name) && <div style={{ marginBottom: 6 }}>Spouse: <b>{profile.spouse_name}</b></div>}
                {!isBlank(profile?.address) && <div style={{ marginTop: 8 }}>Address: <div style={{ color: '#333' }}>{profile.address}</div></div>}
                {!isBlank(profile?.bank_details) && <div style={{ marginTop: 8 }}>Bank: <div style={{ color: '#333' }}>{renderBankDetails(profile.bank_details)}</div></div>}

                {/* show only totals on dashboard; hide if total is zero */}
                {assetsTotal > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <button
                      onClick={() => setShowAssetsOpen(true)}
                      style={{ background: 'none', border: 'none', padding: 0, color: '#1976d2', cursor: 'pointer', fontWeight: 700 }}
                      title="View asset details"
                    >
                      Assets: {assetsTotal.toLocaleString()}
                    </button>
                  </div>
                )}

                {liabilitiesTotal > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <button
                      onClick={() => setShowLiabsOpen(true)}
                      style={{ background: 'none', border: 'none', padding: 0, color: '#1976d2', cursor: 'pointer', fontWeight: 700 }}
                      title="View liability details"
                    >
                      Liabilities: {liabilitiesTotal.toLocaleString()}
                    </button>
                  </div>
                )}

                {renderOtherInfo(profile?.other_info)}

                {/* Consultant shown last in profile section */}
                {assignedConsultant && (
                  <div style={{ marginTop: 12, borderTop: '1px dashed #e6eef8', paddingTop: 10 }}>
                    <div style={{ fontWeight: 700, marginBottom: 6 }}>Consultant</div>
                    <div style={{ fontWeight: 600 }}>{assignedConsultant.name || assignedConsultant.full_name || assignedConsultant.username || assignedConsultant.email}</div>
                    <div style={{ color: '#444', fontSize: 13, marginTop: 6 }}>
                      {(assignedConsultant.mobile || assignedConsultant.phone) && <span style={{ marginRight: 12 }}>Mobile: {assignedConsultant.mobile || assignedConsultant.phone}</span>}
                      {assignedConsultant.email && <span>Email: {assignedConsultant.email}</span>}
                    </div>
                  </div>
                )}

                {/* Subscription information (shown below Consultant) */}
                {(() => {
                  // sources: explicit fetched subscription, profile.subscription (object),
                  // profile.subscriptions (array), or top-level profile.* fields
                  const src = subscription ?? profile?.subscription ?? (Array.isArray(profile?.subscriptions) ? profile.subscriptions[0] : null);
                  const fee = src?.fee ?? profile?.subscription_fee ?? profile?.subscription_fee_amount ?? null;
                  const status = src?.status ?? profile?.subscription_status ?? profile?.payment_status ?? (profile?.is_paid ? 'Paid' : null);
                  const paidAt = src?.payment_date ?? src?.paid_at ?? profile?.payment_date ?? null;
                  const payLink = src?.payment_link ?? profile?.payment_link ?? profile?.payment_url ?? null;
                  const hasAny = (fee !== null && fee !== undefined) || !!status || !!paidAt || !!payLink;
                  if (!hasAny) return null;
                  return (
                    <div style={{ marginTop: 12, borderTop: '1px dashed #e6eef8', paddingTop: 10 }}>
                      <div style={{ fontWeight: 700, marginBottom: 6 }}>Subscription</div>
                      <div style={{ color: '#333', lineHeight: 1.6 }}>
                        {(fee !== null && fee !== undefined) && <div>Subscription fees: <b>{typeof fee === 'number' ? fee.toLocaleString() : fee}</b></div>}
                        {status && <div>Payment status: <b>{status}</b></div>}
                        {paidAt && <div>Payment date: <b>{formatDateOnly(paidAt)}</b></div>}
                        {payLink && (
                          <div>
                            Payment link:{' '}
                            <a href={payLink} target="_blank" rel="noreferrer" style={{ color: '#1976d2' }}>
                              {payLink}
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>

          {/* Documents area - only show when not editing */}
          <div style={{ flex: '3 1 400px' }}>
            {/* Permanent Documents */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '2rem' }}>
              <h3 style={{ color: '#1976d2', margin: 0 }}>Permanent Documents</h3>
              <button onClick={() => openUpload('permanent')} title="Add document" style={{ background: 'none', border: 'none', color: '#0b69ff', cursor: 'pointer', fontSize: 18 }}>
                <FaPlus />
              </button>
            </div>
            {showUploadFor === 'permanent' && (
              <div style={{ marginTop: 12 }}>
                <DocumentUpload category="permanent" targetUserId={viewUserId || profile?.id} onUpload={() => { refreshDocs(); closeUpload(); }} />
              </div>
            )}
            {permanentDocs.length === 0 && <div>No permanent documents found.</div>}
            {permanentDocs.length > 0 && (
              <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
                <thead>
                  <tr style={{ background: '#f0f4f8' }}>
                    <th style={thStyle}>File Type</th>
                    <th style={thStyle}>Remarks</th>
                    <th style={thStyle}>Uploaded</th>
                    <th style={thStyle}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {permanentDocs.map(doc => (
                    <tr key={doc.id} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={tdStyle}>{doc.doc_type}</td>
                      <td style={tdStyle}>{doc.remarks}</td>
                      <td style={tdStyle}>{formatDateOnly(doc.created_at)}</td>
                      <td style={{ ...tdStyle, display: 'flex', gap: 12 }}>
                        <a href={apiUrl + doc.file_url} target="_blank" rel="noopener noreferrer" title="Preview">
                          <FaEye style={{ color: '#1976d2', cursor: 'pointer' }} />
                        </a>
                        <a href={apiUrl + doc.file_url} download title="Download">
                          <FaDownload style={{ color: '#1976d2', cursor: 'pointer' }} />
                        </a>
                        <button style={{ background: 'none', border: 'none', padding: 0 }} onClick={() => handleUpdate(doc)} title="Update">
                          <FaEdit style={{ color: '#1976d2', cursor: 'pointer' }} />
                        </button>
                        <button style={{ background: 'none', border: 'none', padding: 0 }} onClick={() => handleDelete(doc.id)} title="Delete">
                          <FaTrash style={{ color: '#d32f2f', cursor: 'pointer' }} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* Periodical Documents */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '2rem' }}>
              <h3 style={{ color: '#1976d2', margin: 0 }}>Periodical Documents</h3>
              <button onClick={() => openUpload('periodical')} title="Add document" style={{ background: 'none', border: 'none', color: '#0b69ff', cursor: 'pointer', fontSize: 18 }}>
                <FaPlus />
              </button>
            </div>
            {showUploadFor === 'periodical' && (
              <div style={{ marginTop: 12 }}>
                <DocumentUpload category="periodical" targetUserId={viewUserId || profile?.id} onUpload={() => { refreshDocs(); closeUpload(); }} />
              </div>
            )}
            {periodicalDocs.length === 0 && <div>No periodical documents found.</div>}
            {periodicalDocs.length > 0 && (
              <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
                <thead>
                  <tr style={{ background: '#f0f4f8' }}>
                    <th style={thStyle}>File Type</th>
                    <th style={thStyle}>Year</th>
                    <th style={thStyle}>Remarks</th>
                    <th style={thStyle}>Uploaded</th>
                    <th style={thStyle}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {periodicalDocs.map(doc => (
                    <tr key={doc.id} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={tdStyle}>{doc.doc_type}</td>
                      <td style={tdStyle}>{doc.doc_year || '-'}</td>
                      <td style={tdStyle}>{doc.remarks}</td>
                      <td style={tdStyle}>{formatDateOnly(doc.created_at)}</td>
                      <td style={{ ...tdStyle, display: 'flex', gap: 12 }}>
                        <a href={apiUrl + doc.file_url} target="_blank" rel="noopener noreferrer" title="Preview">
                          <FaEye style={{ color: '#1976d2', cursor: 'pointer' }} />
                        </a>
                        <a href={apiUrl + doc.file_url} download title="Download">
                          <FaDownload style={{ color: '#1976d2', cursor: 'pointer' }} />
                        </a>
                        <button style={{ background: 'none', border: 'none', padding: 0 }} onClick={() => handleUpdate(doc)} title="Update">
                          <FaEdit style={{ color: '#1976d2', cursor: 'pointer' }} />
                        </button>
                        <button style={{ background: 'none', border: 'none', padding: 0 }} onClick={() => handleDelete(doc.id)} title="Delete">
                          <FaTrash style={{ color: '#d32f2f', cursor: 'pointer' }} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Assets details modal */}
          {showAssetsOpen && (
            <div style={{ position: 'fixed', inset: 0, background: '#0008', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
              <div style={{ background: '#fff', padding: 20, borderRadius: 8, width: 520, maxWidth: '95%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <h3 style={{ margin: 0 }}>Assets</h3>
                  <button onClick={() => setShowAssetsOpen(false)} style={{ background: 'none', border: 'none', fontSize: 20 }}>✕</button>
                </div>
                <div style={{ maxHeight: '60vh', overflow: 'auto' }}>
                  {assetsList.length === 0 && <div>No assets recorded.</div>}
                  {assetsList.map((a, i) => (
                    <div key={i} style={{ padding: '8px 0', borderBottom: '1px solid #efefef' }}>
                      <div style={{ fontWeight: 600 }}>{a.name || 'Untitled asset'}</div>
                      <div style={{ color: '#555' }}>{a.purchase_date ? formatDateOnly(a.purchase_date) : 'Purchase date: —' } • Amount: {Number(a.amount || 0).toLocaleString()}</div>
                    </div>
                  ))}
                  <div style={{ marginTop: 12, fontWeight: 700 }}>Total: {assetsTotal.toLocaleString()}</div>
                </div>
              </div>
            </div>
          )}

          {/* Liabilities details modal */}
          {showLiabsOpen && (
            <div style={{ position: 'fixed', inset: 0, background: '#0008', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
              <div style={{ background: '#fff', padding: 20, borderRadius: 8, width: 520, maxWidth: '95%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <h3 style={{ margin: 0 }}>Liabilities</h3>
                  <button onClick={() => setShowLiabsOpen(false)} style={{ background: 'none', border: 'none', fontSize: 20 }}>✕</button>
                </div>
                <div style={{ maxHeight: '60vh', overflow: 'auto' }}>
                  {liabilitiesList.length === 0 && <div>No liabilities recorded.</div>}
                  {liabilitiesList.map((l, i) => (
                    <div key={i} style={{ padding: '8px 0', borderBottom: '1px solid #efefef' }}>
                      <div style={{ fontWeight: 600 }}>{l.name || 'Untitled liability'}</div>
                      <div style={{ color: '#555' }}>{l.date ? formatDateOnly(l.date) : 'Date: —' } • Amount: {Number(l.amount || 0).toLocaleString()}</div>
                    </div>
                  ))}
                  <div style={{ marginTop: 12, fontWeight: 700 }}>Total: {liabilitiesTotal.toLocaleString()}</div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};


// expand permanent list
const PERMANENT_DOCS = [
  'NID', 'Pass Port', 'Driving License', 'TIN', 'BIN', 'Marriage Certificate', 'Birth Certificate', 'Land Deed', 'Other'
];
const PERIODICAL_DOCS = [
  'Tax Certificate', 'Tax Return Acknowledgment', 'Tax Return', 'Salary Certificate', 'Bank Statement', 'DPS Statement', 'Shanchoypatra Statement', 'Share Certificate', 'Other'
];

const thStyle = { padding: '8px', textAlign: 'left', fontWeight: 'bold', color: '#1976d2' };
const tdStyle = { padding: '8px', color: '#333' };
const downloadBtnStyle = { color: '#1976d2', textDecoration: 'underline', fontWeight: 'bold' };
const uploadBtnStyle = { background: '#1976d2', color: '#fff', border: 'none', borderRadius: 4, padding: '8px 16px', fontWeight: 'bold', cursor: 'pointer' };
const picBtnStyle = { background: '#1976d2', color: '#fff', border: 'none', borderRadius: 4, padding: '6px 16px', fontWeight: 'bold', cursor: 'pointer', marginTop: 8 };

// helper to render bank_details (accepts JSON string, object or array)
const renderBankDetails = (bank) => {
  if (!bank) return null;
  let parsed = bank;
  try {
    if (typeof bank === 'string' && bank.trim()) parsed = JSON.parse(bank);
  } catch {
    // not JSON — render raw string
    return <div>{String(bank)}</div>;
  }

  const formatAccount = (b) => {
    const accountName = b.account_name ?? b.accountName ?? b.name ?? '-';
    const accountNumber = b.account_number ?? b.account_no ?? b.accountNumber ?? b.accountNo ?? '-';
    const branchName = b.branch_name ?? b.branch ?? b.branchName ?? '-';
    const bankName = b.bank_name ?? b.bankName ?? b.bank ?? '-';
    // commas with a single space after each comma
    return `Account Name: ${accountName}, Account Number: ${accountNumber}, Branch Name: ${branchName}, Bank Name: ${bankName}.`;
  };

  if (Array.isArray(parsed)) {
    return parsed.length === 0 ? null : (
      <div>
        {parsed.map((b, i) => (
          <div key={i} style={{ marginBottom: 6 }}>{formatAccount(b)}</div>
        ))}
      </div>
    );
  }

  if (parsed && typeof parsed === 'object') {
    return <div>{formatAccount(parsed)}</div>;
  }

  return <div>{String(parsed)}</div>;
};

export default UserDashboard;